// Ребаланс-режим (отдельный режим партии, createGame({ rebalance: true })).
// Здесь — ДАННЫЕ режима: правки цен и реестр описаний правок эффектов. Сами правки эффектов живут в скриптах карт
// (tokens.ts, ветки по G.rebalance) и регистрируют своё описание здесь же, рядом с веткой, через rebalanced();
// знания ботов под режим — INFO_REBALANCE в synergy.ts. Подмены на время партии (цены, INFO) применяет
// applyRebalance (state.ts) через хуки registerModeHook — модуль не импортирует движок, циклов нет.
// Метод подбора: docs/card-balance*.md и лаборатория scripts/variant-lab.ts (все решения — по 800 партиям).
import type { CardId, TraitKey } from '../types.ts';

/** Вид правки эффекта: слабее числом / сильнее числом / изменено само правило работы карты */
export type RebalanceKind = 'nerf' | 'buff' | 'rework';

/**
 * Правка карты для режима: что подменяется в её определении на время партии. Правка — это ДРУГАЯ карта,
 * а не сноска к прежней: у неё своя цена и свой текст, и весь интерфейс показывает именно их (сноски
 * «было → стало» живут только в справке режима, где сравнение и есть смысл экрана).
 * Поэтому правка эффекта ОБЯЗАНА нести новый текст: иначе карта будет врать игроку о своём правиле.
 */
export interface CardPatch {
  cost?: number;
  text?: string;
}

/** Описания правок эффектов для справки «что изменено» — заполняются скриптами карт (rebalanced) */
export const REBALANCE_NOTES: Record<CardId, { kind: RebalanceKind; note: string }> = {};

/**
 * Скрипт карты объявляет свою правку режима рядом с веткой G.rebalance: правило, его описание и новый
 * текст карты — в одном месте. text обязателен: правка эффекта без правки текста — карта, которая врёт.
 */
export function rebalanced(cid: CardId, kind: RebalanceKind, note: string, text: string): void {
  REBALANCE_NOTES[cid] = { kind, note };
  const patch = (REBALANCE[cid] ??= {});
  patch.text = text;
}

/** Правки жетонов свойств: у жетона нет цены — подменяется только текст (плюс описание для справки) */
export const REBALANCE_TRAIT_NOTES: Partial<Record<TraitKey, { kind: RebalanceKind; note: string; text: string }>> = {};
/** Свойство объявляет свою правку режима рядом с веткой G.rebalance (как rebalanced() у карт) */
export function rebalancedTrait(key: TraitKey, kind: RebalanceKind, note: string, text: string): void {
  REBALANCE_TRAIT_NOTES[key] = { kind, note, text };
}

/** Что ещё подменяется на время партии в режиме (знания ботов) — регистрируют сами модули, applyRebalance зовёт */
type ModeHook = (on: boolean) => void;
const hooks: ModeHook[] = [];
export function registerModeHook(fn: ModeHook): void {
  hooks.push(fn);
}
export function runModeHooks(on: boolean): void {
  for (const fn of hooks) fn(on);
}

/**
 * История цен пакета по версиям правил (RULES_VERSION в rules.ts): что каждая версия изменила относительно
 * предыдущей; null — правка снята (снова базовая цена). priceTable(v) складывает волны до v включительно.
 * REBALANCE ниже — читаемый текущий пакет с обоснованиями; test/versions.ts сверяет, что priceTable(RULES_VERSION)
 * совпадает с ним. Новая волна = новая запись здесь + правка REBALANCE + RULES_VERSION + 1.
 */
export const PRICE_WAVES: readonly { v: number; set: Partial<Record<CardId, number | null>> }[] = [
  { v: 1, set: { 'palochka-ushatalochka': 22, 'viagrus-tvyordyj-lord': 15, 'volshebnyj-krolik': 19, 'tsirk-bratev-loshashnykh': 9, legendohranitel: 13,
    'suvenirnyj-laryok-v-zale-legend': 3, 'schastlivyj-viabu': 4, 'sladkiy-smurfinyor': 3, 'naryvka-koroleva-genitalnyh-garpij': 8, 'zad-v-budushchee': 12, 'venerina-magolovka': 7, zhrangutan: 6 } },
  { v: 2, set: { trondets: 8, 'kapitan-bartolomyau': 7 } },
  { v: 3, set: { 'chyornaya-ovtsa-chashchob': 3 } },
  { v: 4, set: { 'sladkiy-smurfinyor': 4, 'schastlivyj-viabu': null, 'bratstvo-kontsa': 5, myzhsemya: 8, 'idol-schastlivoj-suchki': 6, 'avada-loshavra': 14,
    'sdelka-so-zhgunom': 14, 'bashnya-radosti': 6, 'vosstanie-iz-zada': 7, 'musornyj-veter': 3, 'povelitel-shkurok': 3 } },
  { v: 5, set: { 'kapitan-bartolomyau': 8, 'sdelka-so-zhgunom': 16, 'schastlivyj-viabu': 8 } },
  { v: 7, set: { trondets: 10, 'bratstvo-kontsa': 4, 'schastlivyj-viabu': 10, 'sherif-muhtar': 8, zozhlozhniki: 8, 'poehavshij-notari-us': 8, 'dlan-tvortsa': 8, 'chipsihoz-arena': 12 } },
  { v: 9, set: { 'ta-samaya-vyalaya-palochka': 13, 'park-vurdalakcionov': 12, 'konduktor-zhmi-na-tormoza': 8, 'pohotlivyj-mayachok': 15 } },
  { v: 10, set: { 'venerina-magolovka': null, 'bratya-loshashnye': 14, 'palochka-miniganochka': 18, nikchemuhazhery: 8, 'malchishnik-v-rlyehe': 13, 'palochka-losharochka': 8, vurdalakar: 5, 'vyalaya-bashnya': 2 } },
];

/** Цены пакета, действовавшие в версии правил v (карты без записи — базовая цена) */
export function priceTable(v: number): Map<CardId, number> {
  const t = new Map<CardId, number>();
  for (const w of PRICE_WAVES) {
    if (w.v > v) break;
    for (const [id, cost] of Object.entries(w.set)) {
      if (cost == null) t.delete(id);
      else t.set(id, cost);
    }
  }
  return t;
}

/** Все карты, чья цена правилась хоть в одной версии (плюс правки, добавленные в REBALANCE на лету — лаборатория) */
export function pricePatchedIds(): CardId[] {
  const ids = new Set<CardId>(Object.keys(REBALANCE));
  for (const w of PRICE_WAVES) for (const id of Object.keys(w.set)) ids.add(id);
  return [...ids];
}

/**
 * Старые записи хранили не версию правил, а номер пакета режима (REBALANCE_VERSION 4–8). Соответствие
 * версиям правил; пакет 4 жил в версиях 5 и 6 (правка жизней лошары номер пакета не меняла) — подбор
 * начинает с 5, а версию 6 проверяет сам (см. replayRecord).
 */
export const LEGACY_PACKAGE: Readonly<Record<number, number>> = { 4: 5, 5: 7, 6: 8, 7: 9, 8: 10 };

export const REBALANCE: Record<CardId, CardPatch> = {
  // ---- нерфы (сильнее своей цены) ----
  /** мгновенная победа добиванием; 58% побед у купивших, остаток +14.3 */
  'palochka-ushatalochka': { cost: 22 }, // 20 → 22
  /** машина вялых палочек; +11.7, n=209 — самый надёжный выброс. Итерации: за 15 → +7.8, за 16 → +12.7 —
   *  дальше цена не рычаг (селекшн: дорогую покупает уже лидирующий, остаток раздувается). Остановились на 15;
   *  эффект правлен отдельно (без ежеходной палочки), на 800 партиях остаток +6 — наблюдаем.
   *  Урок (проверено на Гусыне): картам-«снежкам», чья отдача растёт к концу партии, подъём цены только ПОМОГАЕТ —
   *  поздний богатый покупатель выжимает из них больше; Гусыню поэтому в пакет не берём (10 — её честная цена). */
  'viagrus-tvyordyj-lord': { cost: 15 }, // 13 → 15
  /** 20 урона каждому врагу; +7.5 */
  'volshebnyj-krolik': { cost: 19 }, // 17 → 19
  /** лошарство в плюс; +7.3 */
  'tsirk-bratev-loshashnykh': { cost: 9 }, // 7 → 9
  /** добор+чипсины+вечная защита; +5.9, за 12 остаток даже вырос (+9.6) — сила в механике, цена давит слабо */
  legendohranitel: { cost: 13 }, // 10 → 13
  // ---- бафы (слабее своей цены) ----
  /** −10.2: худшая карта игры по данным; за 2 перебаф (+7.2) → 3 */
  'suvenirnyj-laryok-v-zale-legend': { cost: 3 }, // 4 → 3
  /** −9.6 по корреляции; в режиме за 3 контрфактика дала +7.5% Δточка (перебаф) → возвращаем 4 */
  'sladkiy-smurfinyor': { cost: 4 }, // 5 → 4
  /** −9.4 */
  'naryvka-koroleva-genitalnyh-garpij': { cost: 8 }, // 10 → 8
  /** −8.0 */
  'zad-v-budushchee': { cost: 12 }, // 14 → 12
  /** −7.6 по корреляции; волна 4.5 отменила этот баф: за 7 контрфактика дала +8.0±4.1 (перебаф) */
  /** −7.1; за 5 перебаф (+6.5) → 6 */
  zhrangutan: { cost: 6 }, // 7 → 6
  // ---- волна 2 (лаборатория, подтверждено на 800 партиях; остаток −5 → ~0) ----
  /** −9.4 на 400, −5 на 800 по корреляции; но контрфактика режима дала за 8 уже +7.0±2.6 (перебаф волны 2) */
  trondets: { cost: 10 }, // 11 → 10
  /** −7.4 на 400 по корреляции; за 7 контрфактика режима дала +9.7% Δточка (перебаф) → 8 */
  'kapitan-bartolomyau': { cost: 8 }, // 9 → 8
  // ---- волна 3 ----
  /** −6..−7 к цене на 800; за 3 остаток −1 (докупают охотно, винрейт растёт) */
  'chyornaya-ovtsa-chashchob': { cost: 3 }, // 5 → 3
  // ---- волна 4: контрфактика покупок (tools/branches.ts, 4137 точек, 59 партий; метрика — Δточка,
  //      превышение доли побед ветки над средней веткой той же точки, парно по общим пересевам).
  //      Корреляционные вердикты волн 1–3 она местами опровергла: Счастливый Виабу числился худшим (−9.7),
  //      а причинно даёт +4.7% ещё за свою исходную цену 6 — баф 6→4 отменён (карту покупали отстающие). ----
  /** +6.4% Δточка при цене 2 — самый большой разрыв «сила/цена» в игре: два добора за бесценок;
   *  волна 4.2: за 5 перенерф (−3.5±2.1) → 4 */
  'bratstvo-kontsa': { cost: 4 }, // 2 → 4
  /** +5.9%: добор + скидка 2 на легенды в свой ход + защита — три функции за 6 */
  myzhsemya: { cost: 8 }, // 6 → 8
  /** +5.4% при n=164 (самая частая карта верхушки): мощь + защита с добором и чипсиной за 4 */
  'idol-schastlivoj-suchki': { cost: 6 }, // 4 → 6
  /** +5.0%: добор 2 + лошарство врагу + мощь */
  'avada-loshavra': { cost: 14 }, // 12 → 14
  /** −4.3% за 17; за 14 контрфактика режима дала +6.8% (перебаф) → 16 */
  'sdelka-so-zhgunom': { cost: 16 }, // 17 → 16
  /** волна 4.1: за свою цену 6 карта даёт +8.1% Δточка в режиме (и +4.7% в обычных партиях) — нерф;
   *  волна 4.2: за 8 всё ещё +5.0±3.0 → 10 */
  'schastlivyj-viabu': { cost: 10 }, // 6 → 10
  // ---- волна 4.2: чистый прогон режима на текущем движке (2178 точек, 32 партии). Правки прошлых волн,
  //      попавшие в цель, здесь не трогаются: Идол −0.8, Смурфинёр +0.2, Капитан +0.4, Восстание +0.8,
  //      Авада +2.1, Повелитель шкурок +2.3, Башня радости +2.6 — все в пределах ±3%. ----
  /** +7.4±2.4 (n=50): чипсины и добор за 6 */
  'sherif-muhtar': { cost: 8 }, // 6 → 8
  /** +7.4±2.3 (n=54): дешёвый мотор, покупается рано (ср. ход 27) */
  zozhlozhniki: { cost: 8 }, // 6 → 8
  /** +6.2±2.0 (n=70) */
  'poehavshij-notari-us': { cost: 8 }, // 6 → 8
  /** +6.0±2.9 (n=62) */
  'dlan-tvortsa': { cost: 8 }, // 6 → 8
  /** −4.8±2.3 (n=43): арена за 15 не окупается */
  'chipsihoz-arena': { cost: 12 }, // 15 → 12
  // ---- волна 4.4: проверка волны 4.2 (2624 точки) показала попадание почти всюду (Шериф −0.5,
  //      Зожложники +1.3, Нотари +1.3, Трондец +0.8, Цирк +2.7, Арена −1.6); осталась верхушка ----
  /** +12.1±3.3 (n=26): 7 урона и раздача вялых палочек трупу — лучший размен партии */
  'ta-samaya-vyalaya-palochka': { cost: 13 }, // 11 → 13
  /** +7.8±3.4: постоянка, которая лечит на весь нанесённый урон */
  'park-vurdalakcionov': { cost: 12 }, // 10 → 12
  /** +5.8±2.8 (n=45): «все типы» + добор + защита с перенаправлением за 6 */
  'konduktor-zhmi-na-tormoza': { cost: 8 }, // 6 → 8
  /** +5.8±2.5 (n=33): мощь за каждый жетон — растёт всю партию */
  'pohotlivyj-mayachok': { cost: 15 }, // 13 → 15
  // ---- волна 4.5: проверка 4.4 (2574 точки) — все цели в норме (Та самая вялая −0.7, Легендохранитель −0.9,
  //      Парк −0.6, Мыжсемья −0.4, Виабу +1.9, Гусыня +3.3, Виагрус +3.5 после реза мотора). Осталась
  //      новая верхушка, которую прежние волны не трогали ----
  /** +12.8±3.2 (n=55): защита с добором 3 карт и перенаправлением на лошару — лучшая карта партии */
  'bratya-loshashnye': { cost: 14 }, // 12 → 14
  /** +11.4±4.0: четыре выстрела по 7 и мощь за каждый труп */
  'palochka-miniganochka': { cost: 18 }, // 15 → 18
  /** +8.8±3.3 (n=35) */
  nikchemuhazhery: { cost: 8 }, // 6 → 8
  /** +7.4±4.1: добор всем, неизбежная атака по рукам врагов */
  'malchishnik-v-rlyehe': { cost: 13 }, // 11 → 13
  /** +7.0±3.9 (покупается рано, ср. ход 22) */
  'palochka-losharochka': { cost: 8 }, // 6 → 8
  /** −4.1±1.6 (n=77) */
  vurdalakar: { cost: 5 }, // 6 → 5
  /** −6.7±2.0 (n=33): постоянка, которая копит вялые палочки в минус */
  'vyalaya-bashnya': { cost: 2 }, // 3 → 2
  /** −3.6% (Δпустота 0: не лучше пустого хода) */
  'bashnya-radosti': { cost: 6 }, // 8 → 6
  /** −3.5% */
  'vosstanie-iz-zada': { cost: 7 }, // 9 → 7
  /** −2.6% при n=156, и боты купили её 60 раз — карта переоценена и людьми, и моделью */
  'musornyj-veter': { cost: 3 }, // 4 → 3
  /** −2.1% при n=258 (куплена 53 раза): вялая палочка врагу слабее своей цены */
  'povelitel-shkurok': { cost: 3 }, // 4 → 3
};
